---
title: "Analytics in Chosen"
description: "Reporting on your hiring pipeline: funnels, source effectiveness, time-in-stage, team throughput, and a custom chart builder — all filterable by job and date."
category: "analytics"
keywords:
  - "recruiting analytics"
  - "ATS reporting"
  - "hiring metrics"
  - "pipeline analytics"
  - "recruiting dashboard"
schema: "HowTo"
nav:
  section: "guides"
  order: 60
---

Analytics is the reporting view over your hiring pipeline. It answers questions you'd otherwise count by hand: how many candidates make it from applied to hired, which sources are worth the effort, where people stall, and how long a hire takes. Every report reads from data Chosen already has — nothing here needs separate tracking setup.

You'll find it on the **Analytics** tab in the recruiter app. The view is organized into five tabs — Overview, Pipeline, Quality, Team, and Custom — and a filter bar that scopes every chart at once.

## How the filters work

Three controls at the top of the page apply to the charts below them: a **job** dropdown (defaults to all jobs), a **start date**, and an **end date**. Set a job and you're looking at one role's pipeline; set a date range and you're looking at a window of time. The Overview tab adds a **Compare to previous period** toggle, which puts a delta next to each headline number against the equivalent earlier window.

A few reports ignore the filters by design — pipeline coverage and the job breakdown always describe your current open roles, because "as of right now" is the only reading that makes sense for them.

<Callout type="tip" title="Analytics is read-only — but charts export">
You can't edit anything from Analytics; it only reflects what's in your pipeline. But it isn't a dead end. Hover any chart and a download menu appears with **PNG** and **CSV** options, so individual reports can leave Chosen. For full-table candidate exports, see [data and exports](/reference/data-and-exports).
</Callout>

## In this section

<Steps>
  <li>**[Pipeline and sources](/guides/analytics/pipeline-and-sources)** — the funnel, time-in-stage, drop-off, and which candidate sources actually lead to hires. Start here.</li>
  <li>**[Team and SLA](/guides/analytics/team-and-sla)** — recruiter throughput, how fast candidates get contacted, offer acceptance, and interviewer calibration.</li>
  <li>**[Custom queries and insights](/guides/analytics/custom-queries-and-insights)** — the chart builder for questions the fixed reports don't answer, including ones that use your own custom fields.</li>
</Steps>

Most numbers in Analytics depend on candidates being in the right [pipeline status](/guides/candidates/statuses-and-pipeline) — a stalled board produces a misleading funnel. Keeping statuses current is what makes these reports worth reading.
